import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  Button, IconButton, MenuList, MenuItem, ListItemText,
} from '@material-ui/core';
import { Clear, NotificationsNone } from '@material-ui/icons';
import {
  compact, compose, filter,
  flatten, get, groupBy,
  includes, isNumber, map,
  size, toNumber, uniqBy,
  values,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '../../../common/components';
import messages from '../../messages';
import TargetList from '../TargetList';

const RESENDING_TIME_INTERVAL = 10800000;

const S = {
  Dialog: styled(Dialog)`
    .paper {
      position: relative;
      width: 600px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    margin-bottom: 40px;
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  DialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  Flexbox: styled.div`
    display: flex;
    justify-content: space-between;
    height: 292px;
  `,
  Padder: styled.div`
    width: 30%;
    padding-right: 16px;
  `,
  MenuList: styled(MenuList)`
    overflow: auto;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    outline: none;
  `,
  MenuItem: styled(({ selected, padded, ...otherProps }) => <MenuItem {...otherProps} />)`
    height: 40px;
    min-height: 0;
    padding: 4px ${({ padded }) => (padded ? 32 : 24)}px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
    ${({ selected, theme }) => selected && css`
      color: ${theme.palette.secondary.main};
      font-weight: 700;
    `}
  `,
  ListItemText: styled(({ selected, ...otherProps }) => (
    <ListItemText
      classes={{ primary: 'primary' }}
      {...otherProps}
    />
  ))`
    margin: 0;
    .primary {
      color: ${({ theme }) => theme.palette.grey[800]};
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
      text-align: left;
      ${({ selected }) => selected && css`
        color: ${({ theme }) => theme.palette.secondary.main};
        font-weight: bold;
      `}
    }
  `,
  DialogActions: styled(DialogActions)`
    align-items: flex-end;
  `,
  Clear: styled(Clear)`
    font-size: 32px;
  `,
  Info: styled.p`
    margin: auto 4px;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
    color: ${({ theme }) => theme.palette.status.info};
  `,
  IconButton: styled(IconButton)`
    position: absolute;
    top: 2rem;
    right: 2rem;
    transform: translate(3px, -3px);
  `,
  Button: styled(Button)`
    padding: 7px 16px 7px 12px;
    :disabled {
      color: ${({ theme }) => theme.palette.grey[400]};
    }
  `,
  NotificationsNone: styled(NotificationsNone)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.primary.main};
    font-size: 1.25rem;
    ${({ disabled, theme }) => disabled && css`
      color: ${theme.palette.grey[400]};
    `}
  `,
};

const TargetListDialog = ({
  newsletter, targets, open, onClose, onFetch, onSend, onExited,
}) => {
  const [active, setActive] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState({ type: 'allGroup', value: null });

  const params = useParams();

  const resendableAt = useMemo(() => get('lastSendAt.toDate', newsletter) && Date.parse(newsletter.lastSendAt.toDate()) + RESENDING_TIME_INTERVAL, [newsletter]);

  const disabled = useMemo(() => {
    const lastSendAt = get('lastSendAt', newsletter);

    return (!lastSendAt
      || (isNumber(lastSendAt) && lastSendAt + RESENDING_TIME_INTERVAL >= Date.now())
      || !!(get('lastSendAt.toDate', newsletter) && (resendableAt >= Date.now()))
      || !size(filter(target => !target.recorded, targets.data))
      || get('status', newsletter) === 'closed');
  }, [newsletter, resendableAt, targets.data]);

  const groups = useMemo(() => ({
    customGroups: compose(
      uniqBy(customGroup => customGroup.id),
      compact,
      flatten,
      map(target => target.customGroups),
    )(targets.data),
    clazzGroups: compose(
      uniqBy(clazzGroup => clazzGroup.id),
      map(target => target.clazzGroup),
      filter(target => target.clazzGroup),
    )(targets.data),
  }), [targets.data]);

  const filteredTargets = useMemo(() => {
    switch (selectedGroup.type) {
      case 'gradeGroup': {
        return filter(target => get('grade', target.clazzGroup) === toNumber(selectedGroup.value), targets.data);
      }
      case 'clazzGroup': {
        return filter(target => get('id', target.clazzGroup) === toNumber(selectedGroup.value), targets.data);
      }
      case 'customGroup': {
        return filter(target => includes(toNumber(selectedGroup.value), map(customGroup => customGroup.id, target.customGroups)), targets.data);
      }
      default: {
        return values(targets.data);
      }
    }
  }, [selectedGroup, targets.data]);

  const fetchTargetsByNewsletter = useCallback(() => onFetch({ nid: newsletter.id, sid: params.sid }), [newsletter, onFetch, params.sid]);
  const filterTargets = useCallback(event => setSelectedGroup({
    type: event.currentTarget.dataset.type,
    value: event.currentTarget.dataset.value,
  }), []);
  const switchTab = useCallback(event => setActive(toNumber(event.currentTarget.dataset.value)), []);
  const initState = useCallback(() => {
    setSelectedGroup({ type: 'allGroup', value: null });
    setActive(0);
    onExited();
  }, [onExited]);

  return (
    <S.Dialog
      open={open}
      onClose={onClose}
      onExited={initState}
      onEnter={fetchTargetsByNewsletter}
    >
      <S.DialogTitle>
        <S.Heading>
          <FormattedMessage {...messages.targetListDialogHeading} />
        </S.Heading>
      </S.DialogTitle>
      <S.DialogContent>
        <S.Flexbox>
          <S.Padder>
            <S.MenuList>
              <S.MenuItem
                selected={selectedGroup.type === 'allGroup'}
                onClick={filterTargets}
                data-type="allGroup"
                disableRipple
              >
                <S.ListItemText selected={selectedGroup.type === 'allGroup'}>
                  전체
                </S.ListItemText>
              </S.MenuItem>
              {compose(
                map.convert({ cap: false })((gradeGroup, key) => (
                  <React.Fragment key={key}>
                    <S.MenuItem
                      selected={selectedGroup.type === 'gradeGroup' && selectedGroup.value === key}
                      onClick={filterTargets}
                      data-type="gradeGroup"
                      data-value={key}
                      disableRipple
                    >
                      <S.ListItemText selected={selectedGroup.type === 'gradeGroup' && selectedGroup.value === key}>
                        {`${key}학년`}
                      </S.ListItemText>
                    </S.MenuItem>
                    {map(clazz => (
                      <S.MenuItem
                        key={clazz.id}
                        selected={selectedGroup.type === 'clazzGroup' && toNumber(selectedGroup.value) === clazz.id}
                        onClick={filterTargets}
                        data-type="clazzGroup"
                        data-value={clazz.id}
                        disableRipple
                        padded
                      >
                        {`${clazz.clazzName}반`}
                      </S.MenuItem>
                    ), gradeGroup)}
                  </React.Fragment>
                )),
                groupBy(clazzGroup => clazzGroup.grade),
                get('clazzGroups'),
              )(groups)}
              {compose(
                map(customGroup => (
                  <S.MenuItem
                    key={customGroup.id}
                    selected={selectedGroup.type === 'customGroup' && toNumber(selectedGroup.value) === customGroup.id}
                    onClick={filterTargets}
                    data-type="customGroup"
                    data-value={customGroup.id}
                    disableRipple
                  >
                    {customGroup.name || '미지정'}
                  </S.MenuItem>
                )),
                get('customGroups'),
              )(groups)}
            </S.MenuList>
          </S.Padder>
          <TargetList
            targets={filteredTargets}
            active={active}
            switchTab={switchTab}
          />
        </S.Flexbox>
      </S.DialogContent>
      <S.DialogActions>
        {get('lastSendAt.toDate', newsletter) && (resendableAt >= Date.now())
          && (
            <S.Info>
              {`${dayjs(resendableAt).format('YYYY.MM.DD. HH:mm')} 이후 재전송 가능`}
            </S.Info>
          )}
        <S.IconButton type="button" size="small" onClick={onClose}>
          <S.Clear />
        </S.IconButton>
        <S.Button
          type="button"
          color="primary"
          variant="outlined"
          disabled={disabled}
          onClick={onSend}
        >
          <S.NotificationsNone
            disabled={disabled}
          />
          <FormattedMessage
            {...messages.targetListDialogButton}
          />
        </S.Button>
      </S.DialogActions>
    </S.Dialog>
  );
};

TargetListDialog.propTypes = {
  newsletter: PropTypes.shape({
    id: PropTypes.string,
    lastSendAt: PropTypes.oneOfType([
      PropTypes.shape({
        seconds: PropTypes.number,
        nanoseconds: PropTypes.number,
        toDate: PropTypes.func.isRequired,
      }),
      PropTypes.number,
    ]),
  }),
  targets: PropTypes.shape({
    data: PropTypes.shape({}),
    loading: PropTypes.bool,
    error: PropTypes.instanceOf(Error),
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFetch: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onExited: PropTypes.func.isRequired,
};

TargetListDialog.defaultProps = {
  newsletter: null,
  targets: null,
};

export default TargetListDialog;
